import { useMemo } from "react";
import { STORE_INFO_MAPPING } from "context/SiteSettingsContext/mapping";
import { StoreId } from "@ubnt/store.core/lib/types";
import { useSiteSettings } from "context/SiteSettingsContext";

/**
 * Stores that have buy button enabled
 */
const BUY_BUTTON_ENABLED_STORES = [StoreId.Us, StoreId.Ca];

/**
 * Countries that have buy button enabled
 */
const BUY_BUTTON_ENABLED_COUNTRIES = STORE_INFO_MAPPING.filter((store) =>
  BUY_BUTTON_ENABLED_STORES.includes(store.id),
).flatMap((store) => store.countries);

/**
 * Show buy button based on the GEO IP response.
 * NOTE: We need to show "Buy Now" buttons based on the actual location of the user and not on the website region.
 * For example, if user visits the site from Chile, we default to US region since we don't have a store there.
 * However, we don't want to show Buy Button for users in Chile even though they are defaulted to US region,
 * because we want to show Buy Buttons only for the countries that are considered in the US and CA regions.
 */
export const useBuyButton = (): {
  showBuyButton: boolean;
} => {
  const { store, geoCountryCode } = useSiteSettings();

  const showBuyButton = useMemo(() => {
    /**
     * Exist early and don't show buy button if current store is not in the list of stores
     * that have buy button enabled.
     */
    if (!BUY_BUTTON_ENABLED_STORES.includes(store.id)) {
      return false;
    }

    /**
     * Exist early and don't show buy button if GEO IP country code is not initialized or
     * failed to detect.
     */
    if (!geoCountryCode) {
      return false;
    }

    /**
     * Show buy button if GEO IP country code is in the list of countries that have buy button enabled.
     */
    return BUY_BUTTON_ENABLED_COUNTRIES.includes(geoCountryCode);
  }, [geoCountryCode, store.id]);

  return { showBuyButton };
};
